import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  Stack,
  Paper,
  InputAdornment,
  MenuItem,
  Grid,
  alpha,
  useTheme,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Search, ContentCopy, OpenInNew } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import GlowButton from './shared/GlowButton';
import UtilityHeader from './shared/UtilityHeader';
import StyledTextArea from './shared/StyledTextArea';
import StyledSelect from './shared/StyledSelect';

const FacebookSearchUtility: React.FC = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('fb-search-term') || '');
  const [startDate, setStartDate] = useState<Dayjs | null>(() => {
    const saved = localStorage.getItem('fb-start-date');
    return saved ? dayjs(saved) : null;
  });
  const [endDate, setEndDate] = useState<Dayjs | null>(() => {
    const saved = localStorage.getItem('fb-end-date');
    return saved ? dayjs(saved) : null;
  });
  const [authorFilter, setAuthorFilter] = useState<string>(() => localStorage.getItem('fb-author-filter') || 'all');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    localStorage.setItem('fb-search-term', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (startDate) {
      localStorage.setItem('fb-start-date', startDate.toISOString());
    } else {
      localStorage.removeItem('fb-start-date');
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate) {
      localStorage.setItem('fb-end-date', endDate.toISOString());
    } else {
      localStorage.removeItem('fb-end-date');
    }
  }, [endDate]);

  useEffect(() => {
    localStorage.setItem('fb-author-filter', authorFilter);
  }, [authorFilter]);

  useEffect(() => {
    const generateFacebookUrl = () => {
      try {
        setError('');
        if (!searchTerm.trim() || !startDate || !endDate) {
          setOutput('');
          return;
        }
        if (startDate.isAfter(endDate)) {
          setError('Start date must be before end date');
          setOutput('');
          return;
        }
        const filtersJson: any = {
          "rp_creation_time:0": JSON.stringify({
            "name": "creation_time",
            "args": JSON.stringify({
              "start_year": startDate.year().toString(),
              "start_month": startDate.format('YYYY-MM'),
              "end_year": endDate.year().toString(),
              "end_month": endDate.format('YYYY-MM'),
              "start_day": startDate.format('YYYY-MM-DD'),
              "end_day": endDate.format('YYYY-MM-DD')
            })
          })
        };
        if (authorFilter !== 'all') {
          let authorFilterObj: any = {};
          if (authorFilter === 'friends') authorFilterObj = { "name": "author_friends_feed", "args": "" };
          else if (authorFilter === 'groups_pages') authorFilterObj = { "name": "my_groups_and_pages_posts", "args": "" };
          else if (authorFilter === 'public') authorFilterObj = { "name": "merged_public_posts", "args": "" };
          if (authorFilterObj.name) filtersJson["rp_author:0"] = JSON.stringify(authorFilterObj);
        }
        const filtersBase64 = btoa(JSON.stringify(filtersJson));
        setOutput(`https://www.facebook.com/search/posts?q=${encodeURIComponent(searchTerm)}&filters=${encodeURIComponent(filtersBase64)}`);
      } catch (err) {
        setError('Error generating Facebook search URL');
        setOutput('');
      }
    };
    generateFacebookUrl();
  }, [searchTerm, startDate, endDate, authorFilter]);

  const clearAll = () => {
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
    setAuthorFilter('all');
    setOutput('');
    setError('');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(output);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const openInFacebook = () => {
    if (output) window.open(output, '_blank');
  };

  const setDateRange = (days: number) => {
    const end = dayjs();
    const start = end.subtract(days, 'day');
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <UtilityHeader 
          title="Facebook Search" 
          description="Advanced post search with custom date filters and author targeting." 
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4} sx={{ width: '100%', m: 0 }}>
          <Grid size={{ xs: 12, lg: 7 }} sx={{ p: 0 }}>
            <Paper sx={{ 
              p: 3, 
              bgcolor: 'rgba(255,255,255,0.02)', 
              border: '1px solid', 
              borderColor: 'rgba(255,255,255,0.05)',
              borderRadius: 4 
            }}>
              <Stack spacing={3.5}>
                <StyledTextArea
                  label="Search Term"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="What are you looking for?"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: 'primary.main', mr: 1 }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 3, fontFamily: 'inherit' }
                  }}
                />
                
                <StyledSelect 
                  label="Author Filter" 
                  value={authorFilter} 
                  onChange={(e) => setAuthorFilter(e.target.value as string)}
                >
                  <MenuItem value="all">All Posts</MenuItem>
                  <MenuItem value="friends">Friends Only</MenuItem>
                  <MenuItem value="groups_pages">Groups & Pages</MenuItem>
                  <MenuItem value="public">Public Posts</MenuItem>
                </StyledSelect>

                <Box>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    Quick Date Range
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" gap={1}>
                    {[7, 30, 90, 365].map((days) => (
                      <GlowButton
                        key={days}
                        size="small"
                        variant="outlined"
                        disableGlow
                        onClick={() => setDateRange(days)}
                        sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.1)', color: 'text.secondary' }}
                      >
                        Last {days === 365 ? 'Year' : `${days} Days`}
                      </GlowButton>
                    ))}
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { '& .MuiOutlinedInput-root': { borderRadius: 3 } }
                        },
                      }}
                    />
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      minDate={startDate || undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { '& .MuiOutlinedInput-root': { borderRadius: 3 } }
                        },
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }} sx={{ p: { xs: 0, lg: '0 0 0 32px' } }}>
            <Stack spacing={3}>
              <StyledTextArea
                label="Generated URL"
                accentColor="secondary"
                rows={8}
                value={output}
                placeholder="Fill in the parameters to generate the URL..."
                InputProps={{
                  readOnly: true,
                  sx: { 
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.8rem',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    borderRadius: 4,
                    color: output ? 'primary.main' : 'text.disabled',
                    '& fieldset': { borderColor: output ? 'primary.main' : 'rgba(255,255,255,0.1)' },
                  }
                }}
              />
              
              <Stack spacing={2}>
                <GlowButton
                  fullWidth
                  size="large"
                  onClick={openInFacebook}
                  disabled={!output}
                  startIcon={<OpenInNew />}
                  sx={{ py: 1.5 }}
                >
                  Launch Search
                </GlowButton>
                <GlowButton
                  fullWidth
                  variant="outlined"
                  disableGlow
                  onClick={copyToClipboard}
                  disabled={!output}
                  startIcon={<ContentCopy />}
                >
                  Copy URL
                </GlowButton>
                <GlowButton
                  fullWidth
                  variant="text"
                  disableGlow
                  color="inherit"
                  onClick={clearAll}
                  sx={{ opacity: 0.6 }}
                >
                  Reset Parameters
                </GlowButton>
              </Stack>

              {searchTerm && startDate && endDate && (
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 3, 
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.success.main, 0.2),
                  color: 'success.main'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Ready to search!
                  </Typography>
                  <Typography variant="caption">
                    "{searchTerm}" from {startDate.format('MMM D, YYYY')}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default FacebookSearchUtility;
